package com.yogaaicoach.pages.trainingprogram

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.launch

private const val ErrorDaysMissing = "Please select at least one day"
private const val ErrorDurationMissing = "Please add duration for every selected day"

// Mirrors composeApp's AddNewTrainingViewModel, scoped to all 4 wizard screens now (goal, level,
// age group, schedule) — only createTrainingProgramBasedOnPrevious (the "existing program"
// branch) isn't ported yet.
// Unlike composeApp's version, this doesn't hold List<Level>/List<Goal> display objects (name/
// image/description) — those are Compose-resource-coupled RowItem wrappers that don't belong in
// :core; the jsMain pages hold their own plain display data and derive isSelected by comparing
// against the key StateFlows here, same pattern already used for selectedGoals. scheduleErrorText
// is a plain String here instead of a StringResource, for the same reason.
class AddNewTrainingViewModel(private val service: TrainingProgramService) : ViewModel() {
    private val _selectedGoals = MutableStateFlow<Set<MainGoal>>(emptySet())
    val selectedGoals: StateFlow<Set<MainGoal>> = _selectedGoals

    private val _selectedLevel = MutableStateFlow<LevelKey?>(null)
    val selectedLevel: StateFlow<LevelKey?> = _selectedLevel

    private val _selectedAgeGroup = MutableStateFlow<AgeGroupKey?>(null)
    val selectedAgeGroup: StateFlow<AgeGroupKey?> = _selectedAgeGroup

    private val _weekdays = MutableStateFlow(Weekday.entries.map { WeekdayState(it, false, null) })
    val weekdays: StateFlow<List<WeekdayState>> = _weekdays

    private val _scheduleErrorText = MutableStateFlow<String?>(null)
    val scheduleErrorText: StateFlow<String?> = _scheduleErrorText

    private val _isLoading = MutableStateFlow(false)
    val isLoading: StateFlow<Boolean> = _isLoading

    fun toggleSelectedGoal(key: MainGoal) {
        _selectedGoals.value = if (_selectedGoals.value.contains(key)) {
            _selectedGoals.value - key
        } else {
            _selectedGoals.value + key
        }
    }

    fun selectLevel(key: LevelKey) {
        _selectedLevel.value = key
    }

    fun selectAgeGroup(key: AgeGroupKey) {
        _selectedAgeGroup.value = key
    }

    fun onToggleDayChecked(day: Weekday) {
        _weekdays.value = _weekdays.value.map {
            if (it.weekday == day) it.copy(isChecked = !it.isChecked) else it
        }
    }

    fun updateSelectedDurationAdvanced(day: Weekday, duration: Duration) {
        _weekdays.value = _weekdays.value.map {
            if (it.weekday == day) it.copy(selectedDuration = duration) else it
        }
    }

    fun onSubmitSchedule(onSubmitButtonClicked: () -> Unit) {
        if (hasScheduleErrors()) {
            return
        }
        viewModelScope.launch {
            _isLoading.value = true
            service.createTrainingProgram(
                selectedGoals = _selectedGoals.value,
                selectedLevel = selectedLevel.value!!,
                selectedAgeGroup = selectedAgeGroup.value!!,
                weekdays = _weekdays.value,
            )
            onSubmitButtonClicked()
            _isLoading.value = false
        }
    }

    private fun hasScheduleErrors(): Boolean {
        _scheduleErrorText.value = when {
            weekdays.value.none { it.isChecked } -> ErrorDaysMissing
            weekdays.value.any { it.isChecked && it.selectedDuration == null } -> ErrorDurationMissing
            else -> null
        }
        return scheduleErrorText.value != null
    }

    fun createTrainingProgramBasedOnPrevious(onDone: () -> Unit) {
        viewModelScope.launch {
            service.createTrainingProgramBasedOnPrevious()
            onDone()
        }
    }

    // jsMain-only: this ViewModel is a single long-lived instance for the whole page session
    // (no per-NavBackStackEntry scoping like composeApp's koinViewModel() gets), so without an
    // explicit reset, a second "Create New Program" later in the same session would start
    // pre-filled with the previous run's goal/level/age-group/weekday selections. Call this once
    // a submission has succeeded, before the user can reach the wizard again.
    fun reset() {
        _selectedGoals.value = emptySet()
        _selectedLevel.value = null
        _selectedAgeGroup.value = null
        _weekdays.value = Weekday.entries.map { WeekdayState(it, false, null) }
        _scheduleErrorText.value = null
        _isLoading.value = false
    }
}
